(ns hypnos.dme.panel.patient
  "The patient page.

  Handles new and existing patients."
  (:require [hypnos.dme.core.auth :as auth]
            [hypnos.dme.core.patient :as core-patient]
            [hypnos.dme.subs]
            [hypnos.dme.tile.communications :as communications]
            [hypnos.dme.tile.consult :as consult]
            [hypnos.dme.tile.consults :as consults]
            [hypnos.dme.tile.demographics :as demographics]
            [hypnos.dme.tile.selection :as selection]
            [hypnos.graphql.queries :as queries]
            [re-frame.core :as rf]
            [reagent.core :as reagent]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Identities

(def ^{:private true} defaults
  {;; states
   :loading?                       false ; loading in progress?
   :patient-looked-up?             false ; did we lookup the patient
   :new-patient?                   true  ; a new patient?
   :new-consult?                   false ; create a new consult?
   :new-exchange?                  false ; new consult is an exchange?
   :edit-patient?                  false ; currently editing the patient demographics
   :one-time-message?              false ; sending a one-time message?
   :therapist-assisted-assessment? false ; does the therapist fill out the patient survey?
   ;; consult
   :patient                        {:patient/id                        {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/key                       {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/first-name                {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/last-name                 {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/gender                    {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/dob                       {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/email                     {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false
                                                                        :valid?   nil}
                                    :patient/phone-number              {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false
                                                                        :valid?   nil}
                                    :patient/communication-preferences {:value    #{:communication-channel/sms}
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/image                     {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    ;; current mask
                                    :patient/mask                      {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/sized-mask                {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/selector-type             {:value    :selector-type/therapist
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/selector                  {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    ;; the last consult
                                    :patient/last-consult              {:consult/id                    {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/user                  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        ;; therapist submitted data
                                                                        :consult/service-type          {:value    :service-type/new-setup
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/manufacturer          {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/make                  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/model                 {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/mask-was-comfortable? {:value    true
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/ahi                   {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/modality              {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/cpap                  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/ipap                  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/epap                  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        ;; patient submitted data
                                                                        :consult/weight                {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/take-dentures-out?    {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/sleep-positions       {:value    #{}
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/sleep-motion?         {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/mouth-breathing-level {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/eye-dryness?          {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/claustrophobia-level  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/issues                {:value    #{}
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/chronic-issues?       {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/deviated-septum?      {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/pacemaker?            {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/status                {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}}
                                    :patient/consults                  []}
   :selection                      {:patient/mask          {:value    nil
                                                            :dirty?   false
                                                            :touched? false}
                                    :patient/selector-type {:value    :selector-type/therapist
                                                            :dirty?   false
                                                            :touched? false}
                                    :patient/selector      {:value    nil
                                                            :dirty?   false
                                                            :touched? false}}
   :recommendations                {:system []
                                    :note   nil
                                    :custom {:manufacturer  {:value    nil
                                                             :dirty?   false
                                                             :touched? false}
                                             :make          {:value    nil
                                                             :dirty?   false
                                                             :touched? false}
                                             :model         {:value    nil
                                                             :dirty?   false
                                                             :touched? false}
                                             :selector-type {:value    :selector-type/therapist
                                                             :dirty?   false
                                                             :touched? false}}}})

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- default-patient-timezone
  [state org]
  (when-not (get-in @state [:patient :patient/timezone :value])
    (swap! state
           assoc-in [:patient :patient/timezone :value]
           (:organization/timezone org))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Panel

(defn panel
  "The patient panel"
  [{:keys [_patient-id]}]
  (let [patient (rf/subscribe [:dme/patient])
        state   (reagent/atom defaults)]
    (rf/dispatch-sync [:dme/reset-patient])
    (fn [{:keys [patient-id]}]
      (if (and (not patient-id) (:patient/id @patient))
        (rf/dispatch-sync [:dme/redirect-to-patient-page-with-id])
        (rf/dispatch-sync [:dme/subscribe-recommend-masks]))
      (if patient-id
        (when-not @patient
          (rf/dispatch [:amplify/query
                        queries/get-patient-by-id
                        {:id patient-id}
                        :dme/set-patient
                        :dme/error]))
        (swap! state assoc :new-consult? (nil? (:patient/id @patient))))
      (let [auth         (rf/subscribe [:dme/auth])
            catalog      (rf/subscribe [:dme/catalog])
            organization (rf/subscribe [:dme/organization])
            _            (default-patient-timezone state @organization)
            patient      (rf/subscribe [:dme/patient])
            loading?     (rf/subscribe [:dme/gql-request-in-progress?])
            _            (when-not @loading? (core-patient/merge-patient-into-state state @patient @catalog))
            new-patient? (core-patient/new-patient? state)
            new-consult? (:new-consult? @state)]
        [:section.is-main-section
         [:div.section [demographics/tile state]
          ;; new consult data tile
          (when (or new-patient?
                    (and (not new-patient?)
                         (:patient-looked-up? @state)
                         new-consult?
                         (not (core-patient/exchange? @patient))
                         (contains? #{:consult-state/completed :consult-state/canceled :consult-state/expired}
                                    (get-in @state [:patient :patient/last-consult :consult/status :value]))))
            [consult/tile state])
          ;; mask selection
          (when (and (auth/authorized? @auth :select-recommendation)
                     (or (= :consult-state/pending (get-in @state [:patient :patient/last-consult :consult/status :value]))
                         (and (core-patient/exchange? @patient)
                              new-consult?))
                     (seq (get-in @state [:recommendations :system])))
            [selection/mask-tile state])
          ;; other mask
          (when (and (auth/authorized? @auth :select-recommendation)
                     (= :consult-state/pending (get-in @state [:patient :patient/last-consult :consult/status :value]))
                     (seq (get-in @state [:recommendations :system])))
            [selection/custom-mask-tile state])
          ;; prior consults
          [consults/prior-consults-tile state]
          ;; communications
          [communications/tile]]]))))
